import { create } from "zustand"
import { db } from "@/lib/firebase"
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  writeBatch,
  getDocs,
} from "firebase/firestore"
import type { CalendarEvent } from "@/types/event"
import { useAuthStore } from "@/store/authStore"
import { toast } from "@/hooks/use-toast"
import { format, parseISO, differenceInYears } from "date-fns"

// ==================== ПОЛНЫЙ МАППИНГ ВСЕХ 89 СУБЪЕКТОВ РФ ====================
const regionNameMap: Record<string, string> = {
  "RU-AD": "Республика Адыгея",
  "RU-AL": "Республика Алтай",
  "RU-ALT": "Алтайский край",
  "RU-AMU": "Амурская область",
  "RU-ARK": "Архангельская область",
  "RU-AST": "Астраханская область",
  "RU-BA": "Республика Башкортостан",
  "RU-BEL": "Белгородская область",
  "RU-BRY": "Брянская область",
  "RU-BU": "Республика Бурятия",
  "RU-CE": "Чеченская Республика",
  "RU-CHE": "Челябинская область",
  "RU-CHU": "Чукотский автономный округ",
  "RU-CR": "Республика Крым",
  "RU-CU": "Чувашская Республика",
  "RU-DA": "Республика Дагестан",
  "RU-DON": "Донецкая Народная Республика",
  "RU-HR": "Херсонская область",
  "RU-IN": "Республика Ингушетия",
  "RU-IRK": "Иркутская область",
  "RU-IVA": "Ивановская область",
  "RU-KAM": "Камчатский край",
  "RU-KB": "Кабардино-Балкарская Республика",
  "RU-KC": "Карачаево-Черкесская Республика",
  "RU-KDA": "Краснодарский край",
  "RU-KEM": "Кемеровская область",
  "RU-KGD": "Калининградская область",
  "RU-KGN": "Курганская область",
  "RU-KHA": "Хабаровский край",
  "RU-KHM": "Ханты-Мансийский автономный округ",
  "RU-KIR": "Кировская область",
  "RU-KK": "Республика Хакасия",
  "RU-KL": "Республика Калмыкия",
  "RU-KLU": "Калужская область",
  "RU-KO": "Республика Коми",
  "RU-KOS": "Костромская область",
  "RU-KR": "Республика Карелия",
  "RU-KRS": "Курская область",
  "RU-KYA": "Красноярский край",
  "RU-LEN": "Ленинградская область",
  "RU-LIP": "Липецкая область",
  "RU-LUG": "Луганская Народная Республика",
  "RU-MAG": "Магаданская область",
  "RU-ME": "Республика Марий Эл",
  "RU-MO": "Республика Мордовия",
  "RU-MOS": "Московская область",
  "RU-MOW": "Москва",
  "RU-MUR": "Мурманская область",
  "RU-NEN": "Ненецкий автономный округ",
  "RU-NGR": "Новгородская область",
  "RU-NIZ": "Нижегородская область",
  "RU-NVS": "Новосибирская область",
  "RU-OMS": "Омская область",
  "RU-ORE": "Оренбургская область",
  "RU-ORL": "Орловская область",
  "RU-PER": "Пермский край",
  "RU-PNZ": "Пензенская область",
  "RU-PRI": "Приморский край",
  "RU-PSK": "Псковская область",
  "RU-ROS": "Ростовская область",
  "RU-RYA": "Рязанская область",
  "RU-SA": "Республика Саха (Якутия)",
  "RU-SAK": "Сахалинская область",
  "RU-SAM": "Самарская область",
  "RU-SAR": "Саратовская область",
  "RU-SE": "Севастополь",
  "RU-SEV": "Севастополь",
  "RU-SMO": "Смоленская область",
  "RU-SPE": "Санкт-Петербург",
  "RU-STA": "Ставропольский край",
  "RU-SVE": "Свердловская область",
  "RU-TA": "Республика Татарстан",
  "RU-TAM": "Тамбовская область",
  "RU-TOM": "Томская область",
  "RU-TUL": "Тульская область",
  "RU-TVE": "Тверская область",
  "RU-TY": "Республика Тыва",
  "RU-TYU": "Тюменская область",
  "RU-UD": "Удмуртская Республика",
  "RU-ULY": "Ульяновская область",
  "RU-VGG": "Волгоградская область",
  "RU-VLA": "Владимирская область",
  "RU-VLG": "Вологодская область",
  "RU-VOR": "Воронежская область",
  "RU-YAN": "Ямало-Ненецкий автономный округ",
  "RU-YAR": "Ярославская область",
  "RU-YEV": "Еврейская автономная область",
  "RU-ZAB": "Забайкальский край",
  "RU-ZP": "Запорожская область",
};

let unsubscribe: (() => void) | null = null
let syncDone = false

// Правильное склонение "год / года / лет"
const getYearsWord = (years: number): string => {
  if (years % 10 === 1 && years % 100 !== 11) return "год";
  if (years % 10 >= 2 && years % 10 <= 4 && (years % 100 < 10 || years % 100 >= 20)) return "года";
  return "лет";
};

interface CalendarEventState {
  events: CalendarEvent[]
  loading: boolean
  init: () => void
  addEvent: (event: Omit<CalendarEvent, "id">) => Promise<void>
  deleteEvent: (id: string) => Promise<void>
  deleteByTaskId: (taskId: string) => Promise<void>
  syncRegionMilestones: () => Promise<void>
  cleanup: () => void
}

export const useCalendarEventStore = create<CalendarEventState>((set, get) => ({
  events: [],
  loading: false,

  init: () => {
    const user = useAuthStore.getState().user
    if (!user) return

    set({ loading: true })

    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }

    const q = query(collection(db, "calendarEvents"))

    unsubscribe = onSnapshot(
      q,
      (snap) => {
        const currentUserId = user.id.toString()
        const allEvents = snap.docs.map((d) => ({ id: d.id, ...d.data() } as CalendarEvent))

        const filtered = allEvents.filter((ev) => {
          if (ev.type === "holiday") return true
          if (["leader_birthday", "leader_appointment", "branch_opening"].includes(ev.type || "")) return true
          return ev.createdBy === currentUserId
        })

        set({ events: filtered, loading: false })
        console.log(`📥 Получено событий из Firestore: ${filtered.length}`)
      },
      (err) => {
        console.error("Calendar events error:", err)
        toast({ title: "Ошибка загрузки событий", variant: "destructive" })
        set({ loading: false })
      }
    )

    if (!syncDone) {
      syncDone = true
      setTimeout(() => {
        get().syncRegionMilestones()
      }, 600)
    }
  },

  syncRegionMilestones: async () => {
    try {
      console.log("🔄 Начало синхронизации дат из регионов...")

      const regionsSnap = await getDocs(collection(db, "regions"))
      const batch = writeBatch(db)
      const now = new Date()
      let updatedCount = 0

      const safeParseDate = (dateStr: any): Date | null => {
        if (!dateStr || typeof dateStr !== "string" || dateStr.trim() === "") return null
        try {
          const parsed = parseISO(dateStr.trim())
          return isNaN(parsed.getTime()) ? null : parsed
        } catch {
          return null
        }
      }

      for (const docSnap of regionsSnap.docs) {
        const region = docSnap.data() as any
        const regionId = docSnap.id

        const regionDisplayName = regionNameMap[regionId] || 
                                 region.name || 
                                 region.title || 
                                 `Регион ${regionId}`;

        const leaderName = region.leaderName || "Не указан"

        // 1. День рождения руководителя
        const birthDate = safeParseDate(region.birthDate)
        if (birthDate) {
          let nextDate = new Date(now.getFullYear(), birthDate.getMonth(), birthDate.getDate())
          if (nextDate < now) nextDate.setFullYear(nextDate.getFullYear() + 1)

          const formattedBirthDate = format(birthDate, "dd.MM.yyyy")

          const eventId = `region-${regionId}-leader-birthday`

          batch.set(doc(db, "calendarEvents", eventId), {
            id: eventId,
            type: "leader_birthday",
            title: `🎂 День рождения руководителя РО - ${leaderName}, ${regionDisplayName}`,
            subtitle: leaderName,
            date: format(nextDate, "yyyy-MM-dd"),
            isPublic: true,
            description: `${formattedBirthDate}`,
            regionId,
            createdBy: "system",
            createdAt: serverTimestamp(),
          }, { merge: true })
          updatedCount++
        }

        // 2. Годовщина назначения руководителя
        const appointDate = safeParseDate(region.appointDate)
        if (appointDate) {
          let nextDate = new Date(now.getFullYear(), appointDate.getMonth(), appointDate.getDate())
          if (nextDate < now) nextDate.setFullYear(nextDate.getFullYear() + 1)

          const yearsOfService = differenceInYears(now, appointDate)
          const yearsWord = getYearsWord(yearsOfService)
          const formattedAppointDate = format(appointDate, "dd.MM.yyyy")

          const eventId = `region-${regionId}-leader-appointment`

          batch.set(doc(db, "calendarEvents", eventId), {
            id: eventId,
            type: "leader_appointment",
            title: `📅 Годовщина назначения руководителя — ${leaderName}, ${regionDisplayName}, ${yearsOfService} ${yearsWord}`,
            subtitle: leaderName,
            date: format(nextDate, "yyyy-MM-dd"),
            isPublic: true,
            description: `${formattedAppointDate}`,
            regionId,
            createdBy: "system",
            createdAt: serverTimestamp(),
          }, { merge: true })
          updatedCount++
        }

        // 3. Годовщина открытия отделения
        const deptDate = safeParseDate(region.deptDate)
        if (deptDate) {
          let nextDate = new Date(now.getFullYear(), deptDate.getMonth(), deptDate.getDate())
          if (nextDate < now) nextDate.setFullYear(nextDate.getFullYear() + 1)

          const formattedDeptDate = format(deptDate, "dd.MM.yyyy")

          const eventId = `region-${regionId}-opening`

          batch.set(doc(db, "calendarEvents", eventId), {
            id: eventId,
            type: "branch_opening",
            title: `🏛️ Годовщина открытия отделения — ${regionDisplayName}`,
            date: format(nextDate, "yyyy-MM-dd"),
            isPublic: true,
            description: `${formattedDeptDate}`,
            regionId,
            createdBy: "system",
            createdAt: serverTimestamp(),
          }, { merge: true })
          updatedCount++
        }
      }

      await batch.commit()
      console.log(`✅ Синхронизация завершена. Обновлено/создано событий: ${updatedCount}`)

      get().cleanup()
      setTimeout(() => {
        get().init()
      }, 1200)

    } catch (error) {
      console.error("❌ Ошибка синхронизации дат из регионов:", error)
      toast({ title: "Не удалось подтянуть даты из регионов", variant: "destructive" })
    }
  },

  addEvent: async (newEvent) => {
    const user = useAuthStore.getState().user
    if (!user) {
      toast({ title: "Пользователь не авторизован", variant: "destructive" })
      return
    }

    const currentUserId = user.id.toString()

    if (newEvent.type !== "holiday" && newEvent.createdBy && newEvent.createdBy !== currentUserId) {
      toast({ title: "Нельзя создавать событие от чужого имени", variant: "destructive" })
      return
    }

    try {
      const dataToSave = { ...newEvent, createdAt: serverTimestamp() }

      if (newEvent.type !== "holiday") {
        dataToSave.createdBy = currentUserId
      }

      await addDoc(collection(db, "calendarEvents"), dataToSave)
      toast({ title: "Событие добавлено" })
    } catch (err) {
      console.error("Ошибка добавления события:", err)
      toast({ title: "Не удалось добавить событие", variant: "destructive" })
    }
  },

  deleteEvent: async (id) => {
    const event = get().events.find(e => e.id === id)
    const user = useAuthStore.getState().user
    if (!event || !user) return

    const currentUserId = user.id.toString()

    if (event.type !== "holiday" && event.createdBy !== currentUserId) {
      toast({ title: "Нельзя удалить чужое событие", variant: "destructive" })
      return
    }

    try {
      await deleteDoc(doc(db, "calendarEvents", id))
      toast({ title: "Событие удалено" })
    } catch (err) {
      console.error("Ошибка удаления события:", err)
      toast({ title: "Не удалось удалить событие", variant: "destructive" })
    }
  },

  deleteByTaskId: async (taskId: string) => {
    const currentEvents = get().events
    const eventToDelete = currentEvents.find(e => e.sourceTaskId === taskId)
    if (!eventToDelete) return

    try {
      await deleteDoc(doc(db, "calendarEvents", eventToDelete.id))
      toast({ title: "Связанное событие в календаре удалено" })
    } catch (err) {
      console.error("Ошибка удаления связанного события:", err)
      toast({ title: "Не удалось удалить связанное событие", variant: "destructive" })
    }
  },

  cleanup: () => {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  },
}))